import { Assert } from "../../Assert";
import { DataSource } from "../../Data/DataSource";
import { PartialNullable } from "../../Extensions";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyBoolean, PropertyString } from "../../RadObject";
import { IPropertyEditor, PropertyEditor, PropertyEditorPropertyValues } from "../Controls/PropertyEditor";
import { ComboBox } from "./ComboBox";
import { NAMESPACE } from "./_Namespace";

export type PropertyEditorComboBoxPropertyValues =
    PropertyEditorPropertyValues
    & {
        allowEmptySelection: boolean;
        emptySelectionText: string;
        listDataSource: DataSource;
        listDataSourceDescriptionProperty: string;
        listDataSourceValueProperty: string;
    };

export type PropertyEditorComboBoxProperties = {
    [name in keyof PropertyEditorComboBoxPropertyValues]: Property<PropertyEditorComboBoxPropertyValues[name]>
};

export interface IPropertyEditorComboBox extends IPropertyEditor, PropertyEditorComboBoxProperties
{
}

export class PropertyEditorComboBox extends PropertyEditor implements IPropertyEditorComboBox
{
    // #region Static

    public static override classNamespace = NAMESPACE + "PropertyEditorComboBox";
    public static override className = "PropertyEditorComboBox";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<PropertyEditorComboBoxPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                allowEmptySelection: true,
                createEditorControl: () => new ComboBox(),
                listDataSourceDescriptionProperty: "description",
                listDataSourceValueProperty: "value",
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Properties

    public allowEmptySelection!: PropertyBoolean;
    public emptySelectionText!: PropertyString;
    public listDataSource!: Property<DataSource>;
    public listDataSourceDescriptionProperty!: PropertyString;
    public listDataSourceValueProperty!: PropertyString;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<PropertyEditorComboBoxPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-property-editor-combo-box");

        // Trigger Property Changes
        this.allowEmptySelection.triggerChange();
        this.emptySelectionText.triggerChange();
        //this.listDataSource.triggerChange();
        //this.listDataSourceDescriptionProperty.triggerChange();
        //this.listDataSourceValueProperty.triggerChange();
    }

    // #endregion

    // #region Protected Methods

    protected _allowEmptySelectionChanged(change: PropertyChange<boolean>)
    {
        const
            editorControl = this.editorControl.get();

        Assert.isInstanceOf(ComboBox, editorControl, "editorControl");
        editorControl.allowEmptySelection.set(change.currentValue);
    }
    protected _emptySelectionTextChanged(change: PropertyChange<string>)
    {
        const
            editorControl = this.editorControl.get();

        Assert.isInstanceOf(ComboBox, editorControl, "editorControl");
        editorControl.emptySelectionText.set(change.currentValue);
    }
    protected _listDataSourceChanged(change: PropertyChange<DataSource>)
    {
        const
            editorControl = this.editorControl.get();

        Assert.isInstanceOf(ComboBox, editorControl, "editorControl");
        editorControl.listDataSource.set(change.currentValue);
    }
    protected _listDataSourceDescriptionPropertyChanged(change: PropertyChange<string>)
    {
        const
            editorControl = this.editorControl.get();

        Assert.isInstanceOf(ComboBox, editorControl, "editorControl");
        editorControl.listDataSourceDescriptionProperty.set(change.currentValue);
    }
    protected _listDataSourceValuePropertyChanged(change: PropertyChange<string>)
    {
        const
            editorControl = this.editorControl.get();

        Assert.isInstanceOf(ComboBox, editorControl, "editorControl");
        editorControl.listDataSourceValueProperty.set(change.currentValue);
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: PropertyEditorComboBoxPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.allowEmptySelection = new PropertyBoolean(this, propertyValues.allowEmptySelection, this._allowEmptySelectionChanged);
        this.emptySelectionText = new PropertyString(this, propertyValues.emptySelectionText, this._emptySelectionTextChanged);
        this.listDataSource = new Property<DataSource>(this, "System.Object, System.Private.CoreLib", propertyValues.listDataSource, this._listDataSourceChanged);
        this.listDataSourceDescriptionProperty = new PropertyString(this, propertyValues.listDataSourceDescriptionProperty, this._listDataSourceDescriptionPropertyChanged);
        this.listDataSourceValueProperty = new PropertyString(this, propertyValues.listDataSourceValueProperty, this._listDataSourceValuePropertyChanged);
    }

    // #endregion

    // #endregion
}
